import React from 'react'
import PropTypes from 'prop-types'
import {ReactComponent as PercentageIcon} from '@shop/icon-fonts/optimized/percent-mid.svg'
import styled, {css} from 'styled-components'

import AppContext from '../../context'
import {media} from '../../theme/config'
import Link from '../Link'
import {RedIconRightMargin} from '../Icon/Icon.styled'

export const CATEGORY_TYPES = {
  ANIMAL: 'ANIMAL',
  BRANDS: 'BRANDS',
  OFFERS: 'OFFERS'
}

export const Flyout = styled.div`
  position: absolute;
  display: ${({show}) => (show ? 'block' : 'none')};
  top: 100%;
  left: 15px;
  right: 15px;
  background: ${({theme}) => theme.colors.greyscale.white};
  padding: 20px;
  box-shadow: 0 4px 15px 0 rgba(0,0,0,.3);
  border-bottom: 3px solid ${({theme}) => theme.colors.primary.base};
  overflow: hidden;
  border-bottom-right-radius: 3px;
  border-bottom-left-radius: 3px;
`

const halfPanel = ({specials}) => {
  if (specials) {
    return css`
      flex-basis: 100%;  
      max-width: 100%;  
      width: 100%;
      
      @media ${media.tablet} {
        flex-basis: 70%;  
        max-width: 70%;  
        width: 70%;
      }
      
      @media ${media.desktop} {
        flex-basis: 50%;  
        max-width: 50%;  
        width: 50%;
      }
    `
  }
  return ''
}

export const AnimalPanel = styled.ul`
  display: flex;
  flex-wrap: wrap;
  padding: 0;
  margin: 0;
  list-style-type: none;
  
  ${halfPanel}
`

export const AnimalPanelGroup = styled.li`
  max-width: 50%;
  width: 50%;
  flex-basis: 50%;
  
  &:nth-child(odd) {
    padding-right: 15px;
  }
  
  &:nth-child(3),
  &:nth-child(4) {
    margin-top: 30px;
  }

  @media ${media.tablet} {
    max-width: 33%;
    width: 33%;
    flex-basis: 33%;
  
    &:nth-child(1),
    &:nth-child(2),
    &:nth-child(4) {
      padding-right: 15px;
    }
  
    &:nth-child(3) {
      margin-top: 0;
    }
  }

  @media ${media.desktop} {
    max-width: 25%;
    width: 25%;
    flex-basis: 25%;
  
    &:nth-child(4) {
      margin-top: 0;
    }
  
    &:not(:last-child) {
      padding-right: 15px;
    }
  }
`

export const SpecialsPanelGroup = styled.li`
  max-width: 50%;
  width: 50%;
  flex-basis: 50%;
  
  &:nth-child(odd) {
    padding-right: 15px;
  }
`

export const Grouped = styled.ul`
  padding: 0;
  margin: 10px 0 15px; 
  list-style-type: none;
`

export const GroupHeader = styled.div`
  margin-bottom: 10px;
  ${halfPanel}
`

export const GroupItem = styled.li`
  margin-bottom: 10px;
`

export const TopBrandsPanel = styled.ul`
  padding: 0;
  list-style-type: none;
  columns: 2;

  @media ${media.tablet} {
    columns: 3;
  }

  @media ${media.desktop} {
    columns: 4;
  }
`

const SpecialOffersWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`

export const CategoryPropType = PropTypes.shape({
  type: PropTypes.oneOf(Object.values(CATEGORY_TYPES)).isRequired,
  path: PropTypes.string.isRequired,
  extId: PropTypes.number.isRequired,
  description: PropTypes.string.isRequired,
  children: PropTypes.arrayOf(PropTypes.shape({
    children: PropTypes.arrayOf(PropTypes.shape({
      title: PropTypes.string.isRequired,
      path: PropTypes.string.isRequired
    })),
    title: PropTypes.string.isRequired,
    path: PropTypes.string.isRequired
  })).isRequired,
  specials: PropTypes.array
})

const SimpleLink = ({title, path, children, isOfferLink, ...props}) => (
  <GroupItem>
    <Link href={path} isOfferLink {...props}>
      {children}
      {title}
    </Link>
  </GroupItem>
)

SimpleLink.propTypes = {
  title: PropTypes.string.isRequired,
  path: PropTypes.string.isRequired,
  type: PropTypes.string,
  size: PropTypes.oneOf(['small', 'default']),
  bold: PropTypes.bool,
  ellipsis: PropTypes.bool,
  isOfferLink: PropTypes.bool
}

SimpleLink.defaultProps = {
  type: 'primary',
  size: 'default',
  bold: true,
  ellipsis: true,
  isOfferLink: false
}

export const AnimalFlyout = ({category}) => {
  const {description, path, children, specials} = category
  const groupedItems = []
  const restItems = []

  children.forEach(child => {
    if (child.children && child.children.length) {
      groupedItems.push({
        title: child.title,
        path: child.path,
        children: child.children
      })
    } else {
      restItems.push({
        title: child.title,
        path: child.path
      })
    }
  })

  return (
    <>
      <GroupHeader><Link withPadding={false} size="default" href={path}>{description}</Link></GroupHeader>
      <AnimalPanel>
        {groupedItems.map((main, mainIndex) => (
          <AnimalPanelGroup key={mainIndex}>
            <GroupHeader><Link withPadding={false} size="default" href={main.path}>{main.title}</Link></GroupHeader>
            <Grouped>
              {main.children.map((item, index) => (<SimpleLink key={index} bold={false} title={item.title} path={item.path} />))}
            </Grouped>
            <footer>
              <AppContext.Consumer>
                {({translations}) => (
                  <Link withPadding={false} size="default" href={main.path}>
                    {translations.get('shop.header.categories.all.products.link')}
                  </Link>
                )}
              </AppContext.Consumer>
            </footer>
          </AnimalPanelGroup>
        ))}
        <AnimalPanelGroup>
          <Grouped>
            {restItems.map((item, index) => (<SimpleLink key={index} withPadding={false} title={item.title} path={item.path} />))}
          </Grouped>
        </AnimalPanelGroup>
        <AnimalPanelGroup>
          <Grouped>
            {specials && specials.map((item, index) => (<SimpleLink key={index} type="offer" withPadding={false} title={item.title} path={item.path} />))}
          </Grouped>
        </AnimalPanelGroup>
      </AnimalPanel>
    </>
  )
}

AnimalFlyout.propTypes = {
  category: CategoryPropType.isRequired
}

export const TopBrandsFlyout = ({category}) => {
  const {description, path, children} = category

  return (
    <>
      <GroupHeader><Link withPadding={false} size="default" href={path}>{description}</Link></GroupHeader>
      <TopBrandsPanel>
        {children.map((item, index) => (<SimpleLink key={index} bold={false} withPadding={false} title={item.title} path={item.path} />))}
      </TopBrandsPanel>
    </>
  )
}

export const SpecialOffersFlyout = ({category}) => {
  const {description, path, children, specials} = category

  return (
    <SpecialOffersWrapper>
      <GroupHeader specials>
        <Link withPadding={false} size="default" href={path}>{description}</Link>
      </GroupHeader>
      <AnimalPanel specials>
        <SpecialsPanelGroup>
          <Grouped>
            {children.map((item, index) => (<SimpleLink key={index} withPadding={false} title={item.title} path={item.path} />))}
          </Grouped>
        </SpecialsPanelGroup>
        <SpecialsPanelGroup>
          <Grouped>
            {specials.map((item, index) => (
              <SimpleLink key={index} withPadding={false} title={item.title} path={item.path}>
                <RedIconRightMargin>
                  <PercentageIcon />
                </RedIconRightMargin>
              </SimpleLink>
            ))}
          </Grouped>
        </SpecialsPanelGroup>
      </AnimalPanel>
    </SpecialOffersWrapper>
  )
}

AnimalFlyout.propTypes = {
  category: CategoryPropType.isRequired
}

SpecialOffersFlyout.propTypes = {
  category: CategoryPropType.isRequired
}
